// The Prep Notepad's in-memory component buffer shape (spec §4.3) — the
// non-persistent NPReagentPrepLog the wizard edits before OK writes real rows.
// Split out of PrepNotepadDialog so that file exports only its component
// (react-refresh), and so the list page can type its buffer without importing
// the dialog.

/** One component in the buffer. Mirrors NPReagentPrepLog's displayed members;
 *  every measurement is a string because the legacy columns are all nvarchar
 *  (§20 quirk 1) — parsing them here would silently rewrite what was typed. */
import type { ReagentPrepComponent } from './types';

export interface PrepNotepadDraft {
  componentID: number;
  // Stock (mutually exclusive pairs, §6.3) — ids plus the text the combo shows
  vendorStockId: string; vendorStockName: string;
  ltId: string;          ltText: string;        ltExpDate: string;
  labStockId: string;    labStockName: string;
  lltId: string;         lltText: string;       lltExpDate: string;
  solvent: boolean;
  // Chemistry
  calculationApproachId: string;
  w1UnitsId: string; v1UnitsId: string; c1UnitsId: string; v2UnitsId: string; c2UnitsId: string;
  purity: string; density: string; mw: string; eqWt: string; constant: string;
  formula: string;
  weight_g_w1: string; volumeTaken_V1: string; stockConc_C1: string;
  finalVol_V2: string; finalConc_C2: string;
  comment: string;
  // Micro Media
  phCriteria: string; ph: string; autoclave: string; filterSterilization: string;
  sporeGrowth: string; positiveControl: string; negativeControl: string;
}

export function blankDraft(id: number): PrepNotepadDraft {
  return {
    componentID: id,
    vendorStockId: '', vendorStockName: '',
    ltId: '', ltText: '', ltExpDate: '',
    labStockId: '', labStockName: '',
    lltId: '', lltText: '', lltExpDate: '',
    solvent: false,
    calculationApproachId: '',
    w1UnitsId: '', v1UnitsId: '', c1UnitsId: '', v2UnitsId: '', c2UnitsId: '',
    purity: '', density: '', mw: '', eqWt: '', constant: '',
    formula: '',
    weight_g_w1: '', volumeTaken_V1: '', stockConc_C1: '',
    finalVol_V2: '', finalConc_C2: '',
    comment: '',
    phCriteria: '', ph: '', autoclave: '', filterSterilization: '',
    sporeGrowth: '', positiveControl: '', negativeControl: '',
  };
}


/**
 * A Notepad draft as a Components-grid ROW — so components entered for a NEW
 * preparation show in the grid straight after OK, before the first Save writes
 * them. Unit / approach names come from the lookups the Notepad used; LT / LLT
 * expiry dates are shown as entered (the server re-derives them on save).
 */
export function draftToDisplayRow(
  d: PrepNotepadDraft,
  names: { units: Map<string, string>; approaches: Map<string, string> },
): ReagentPrepComponent {
  const n = (s: string) => (s ? s : null);
  const unit = (id: string) => (id ? names.units.get(id) ?? null : null);
  return {
    oid: `pending-${d.componentID}`,
    componentID: d.componentID,
    vendorStockId: n(d.vendorStockId), ltId: n(d.ltId), labStockId: n(d.labStockId), lltId: n(d.lltId),
    calculationApproachId: n(d.calculationApproachId),
    w1UnitsId: n(d.w1UnitsId), v1UnitsId: n(d.v1UnitsId), c1UnitsId: n(d.c1UnitsId),
    v2UnitsId: n(d.v2UnitsId), c2UnitsId: n(d.c2UnitsId),
    vendorStockName: n(d.vendorStockName),
    ltText: n(d.ltText), ltExpDate: n(d.ltExpDate),
    labStockName: n(d.labStockName),
    lltText: n(d.lltText), lltExpDate: n(d.lltExpDate),
    solvent: d.solvent,
    calculationApproachName: d.calculationApproachId ? names.approaches.get(d.calculationApproachId) ?? null : null,
    v1UnitsName: unit(d.v1UnitsId), w1UnitsName: unit(d.w1UnitsId), c1UnitsName: unit(d.c1UnitsId),
    v2UnitsName: unit(d.v2UnitsId), c2UnitsName: unit(d.c2UnitsId),
    stockConc_C1: n(d.stockConc_C1), volumeTaken_V1: n(d.volumeTaken_V1),
    weight_g_w1: n(d.weight_g_w1), finalVol_V2: n(d.finalVol_V2), finalConc_C2: n(d.finalConc_C2),
    purity: n(d.purity), density: n(d.density), mw: n(d.mw), eqWt: n(d.eqWt), constant: n(d.constant),
    formula: n(d.formula), comment: n(d.comment),
    phCriteria: n(d.phCriteria), ph: n(d.ph), autoclave: n(d.autoclave),
    filterSterilization: n(d.filterSterilization), sporeGrowth: n(d.sporeGrowth),
    positiveControl: n(d.positiveControl), negativeControl: n(d.negativeControl),
    workingStdName: null, stockStdName: null,
    cal_Weight_g_w1: null, calWsUnitsName: null, cal_VolumeTaken_V1: null, calV1UnitsName: null,
    cal_FinalVol_V2: null, calV2UnitsName: null, cal_FinalConc_C2: null, calC2UnitsName: null,
    subLLT: null,
  };
}
